"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLiteralOperandValue = exports.mergeOperand = exports.data2Operand = exports.removeArrayOperandItemByIndex = exports.objectCombOperand = exports.isOperandNullValue = exports.getOperandValueType = exports.getObjectOperandProperty = exports.isObjectOperand = exports.isArrayOperand = exports.isLiteralOperand = exports.isOperand = void 0;
/**
 * utils for operand
 */
const immer_1 = __importDefault(require("immer"));
const lodash_1 = require("lodash");
const const_1 = require("./const");
// is input value an operand
const isOperand = (value) => {
    if (value == null)
        return false;
    return typeof value === 'object' && value.type === 'Literal' || value.type === 'Expression';
};
exports.isOperand = isOperand;
// is input value an expression
const isLiteralOperand = (value) => {
    return (0, exports.isOperand)(value) && value.type === 'Literal';
};
exports.isLiteralOperand = isLiteralOperand;
// is input value an array operand
const isArrayOperand = (operand) => {
    return (0, exports.isOperand)(operand) && !(0, exports.isLiteralOperand)(operand) && operand.value.operator === 'newArray';
};
exports.isArrayOperand = isArrayOperand;
// is input value an object operand
const isObjectOperand = (operand) => {
    return (0, exports.isOperand)(operand) && !(0, exports.isLiteralOperand)(operand) && operand.value.operator === 'newObject';
};
exports.isObjectOperand = isObjectOperand;
/**
 * get value of operand-object by key
 * @param objectOperand
 * @param key
 * @param propertySchema
 */
const getObjectOperandProperty = (objectOperand, key, propertySchema) => {
    if ((0, exports.isObjectOperand)(objectOperand)) {
        const keyIndex = objectOperand.value.operands.findIndex((operand, index) => {
            return index % 2 === 0 && typeof operand === 'string' ? key === operand : (0, lodash_1.isEqual)(operand, key);
        });
        if (keyIndex > -1) {
            return objectOperand.value.operands[keyIndex + 1];
        }
        return JSON.parse(JSON.stringify(const_1.EmptyNullOperand));
    }
    // when formData is empty, return default null value
    if (objectOperand == null || Object.keys(objectOperand).length === 0) {
        return JSON.parse(JSON.stringify(const_1.EmptyNullOperand));
    }
    switch (propertySchema.type) {
        case 'object':
            return JSON.parse(JSON.stringify(const_1.EmptyObjectOperand));
        case 'array':
            return JSON.parse(JSON.stringify(const_1.EmptyArrayOperand));
        default:
            return JSON.parse(JSON.stringify(const_1.EmptyNullOperand));
    }
};
exports.getObjectOperandProperty = getObjectOperandProperty;
const getExpressionOperatorReturnType = (operator) => {
    const OperatorReturnTypeMap = {
        newArray: 'array',
        newObject: 'object',
        concatString: 'string',
        getArrayLength: 'number',
        concatParagraph: 'string',
        and: 'boolean',
        or: 'boolean',
        JSONStringify: 'string',
        length: 'number',
    };
    return OperatorReturnTypeMap[operator] || 'null';
};
// get return type of operand
const getOperandValueType = (operand) => {
    if (!(0, exports.isOperand)(operand)) {
        throw Error('must be operand');
    }
    if (operand.type === 'Literal') {
        return typeof operand.value;
    }
    if (operand.type === 'Expression') {
        return getExpressionOperatorReturnType(operand.value.operator);
    }
    throw Error('must be operand');
};
exports.getOperandValueType = getOperandValueType;
const isOperandNullValue = (operand, schema) => {
    // if null
    if (operand.type === 'Literal' && operand.value === null) {
        return true;
    }
    switch (schema.type) {
        case 'object': {
            return Object.keys(schema.properties).some(propertyKey => {
                const propertySchema = schema.properties[propertyKey];
                const propertyValue = (0, exports.getObjectOperandProperty)(operand, propertyKey, propertySchema);
                return (0, exports.isOperandNullValue)(propertyValue, propertySchema);
            });
        }
        case 'array':
            return (0, lodash_1.isEqual)(operand, const_1.EmptyArrayOperand);
        case 'string':
            break;
        case 'boolean':
            break;
        case 'number':
            break;
    }
    return false;
};
exports.isOperandNullValue = isOperandNullValue;
/**
 * merge to the array of operands into one object operand
 * - ['key1',{k11:v11,k12:v12},'key1',{k11:v12,k22:v22},'key2','value22'] =>
 * {
 *   key1: {
 *      k11: v12, // params[0].key1.k11 is overrided by params[2].key1.k11
 *      k12: v12, // params[0].key1 has 'k12' and params[2].key1 has no 'k12', so keep params[0].key1.k12
 *      k22: v22  // params[2].key1 has 'k22' and params[0].key1 has no 'k22', so keep params[2].key1.k22
 *   }
 *   key2: 'value22'
 * }
 * @param operands
 */
const objectCombOperand = (operands) => {
    const newItems = [];
    for (let idx = 0; idx < operands.length; idx += 2) {
        // idx 0 2 4 6 8
        const itemKey = operands[idx]; // 0
        const itemValue = operands[idx + 1]; //1
        // even numbers is key & odd numbers is value
        const newItemKeys = newItems.filter((_item, index) => {
            return index % 2 === 0;
        });
        const itemKeyIndex = newItemKeys.findIndex(key => (0, lodash_1.isEqual)(key, itemKey));
        if (itemKeyIndex > -1) {
            const itemValueIndex = itemKeyIndex * 2 + 1;
            const oldItemValue = newItems[itemValueIndex];
            if ((0, exports.isObjectOperand)(oldItemValue) && (0, exports.isObjectOperand)(itemValue)) {
                newItems[itemValueIndex] = (0, exports.mergeOperand)(oldItemValue, itemValue);
            }
            else {
                newItems[itemValueIndex] = itemValue;
            }
        }
        else {
            newItems.push(itemKey);
            newItems.push(itemValue);
        }
    }
    return newItems;
};
exports.objectCombOperand = objectCombOperand;
const removeArrayOperandItemByIndex = (arrayOperand, index) => {
    return (0, immer_1.default)(arrayOperand, (draft) => {
        if ((0, exports.isOperand)(draft)) {
            draft.value.operands.splice(index, 1);
        }
        return draft;
    });
};
exports.removeArrayOperandItemByIndex = removeArrayOperandItemByIndex;
/**
 * transform an object to an operand
 */
const data2Operand = (data) => {
    if (data == null) {
        return JSON.parse(JSON.stringify(const_1.EmptyNullOperand));
    }
    switch (typeof data) {
        case 'object':
            // array
            if (Array.isArray(data)) {
                return {
                    type: 'Expression',
                    value: {
                        operator: 'newArray',
                        operands: data.map(item => (0, exports.data2Operand)(item))
                    }
                };
            }
            return {
                type: 'Expression',
                value: {
                    operator: 'newObject',
                    operands: Object.keys(data).map(key => [key, (0, exports.data2Operand)(data[key])]).flat()
                }
            };
        case 'string':
        case 'number':
        case 'boolean':
            return {
                type: 'Literal',
                value: data
            };
    }
};
exports.data2Operand = data2Operand;
/**
 * merge two operands into one
 * @param operand1
 * @param operand2
 */
const mergeOperand = (operand1, operand2) => {
    if ((0, exports.isObjectOperand)(operand1) && (0, exports.isObjectOperand)(operand2)) {
        return {
            type: 'Expression',
            value: {
                operator: 'newObject',
                operands: (0, exports.objectCombOperand)([...operand1.value.operands, ...operand2.value.operands])
            }
        };
    }
    return operand1;
};
exports.mergeOperand = mergeOperand;
const getLiteralOperandValue = (operand) => {
    if ((0, exports.isLiteralOperand)(operand)) {
        return operand.value;
    }
    throw Error('must be literal operand');
};
exports.getLiteralOperandValue = getLiteralOperandValue;
//# sourceMappingURL=utils.js.map